import { cloneHpSnapshot, getPanelById } from './panels.js';

function getCombatantLabel(id, panels) {
  const panel = getPanelById(panels, id);
  if (!panel) {
    return `Боец #${id}`;
  }
  // Формируем имя: "Игрок (Существо)" или просто "Существо", если игрок не указан
  const creatureName = panel.name || `#${id}`;
  const ownerName = panel.owner || null;
  
  if (ownerName && ownerName !== creatureName) {
    return `${ownerName} (${creatureName})`;
  }
  return creatureName;
}

function formatBattlePlaybackEntry(entry, panels) {
  if (!entry) return '…';
  const roundPrefix = entry.round != null ? `Раунд ${entry.round}: ` : '';
  switch (entry.event) {
    case 'attack': {
      const attacker = getCombatantLabel(entry.source, panels);
      const target = getCombatantLabel(entry.target, panels);
      const value = entry.value != null ? ` −${Math.round(entry.value)}` : '';
      const crit = entry.breakdown?.crit ? ' (КРИТ!)' : '';
      const element = entry.abilityType ? ` [${entry.abilityType}]` : '';
      const defeated = entry.defeated ? ' 💀' : '';
      // Добавляем информацию об энергии, если есть
      const energyInfo = entry.energy ? ` ⚡(${entry.energy.before}→${entry.energy.after})` : '';
      const abilityInfo = entry.abilityId && entry.abilityId !== 'basic_attack' ? ` [${entry.abilityId}]` : '';
      return `${roundPrefix}⚔️ ${attacker} атакует ${target}${abilityInfo}${element}${value}${crit}${energyInfo}${defeated}`;
    }
    case 'stack_reset':
      return `${roundPrefix}🔄 Волна The Stack перезапустилась`;
    case 'skip': {
      const actor = getCombatantLabel(entry.source, panels);
      let reason = '';
      if (entry.reason === 'insufficient_energy') {
        reason = ` (недостаточно энергии: требуется ${entry.required}, доступно ${entry.available})`;
      } else if (entry.reason === 'ability_on_cooldown') {
        reason = ` (способность на кулдауне: ${entry.cooldownRemaining} хода)`;
      }
      return `${roundPrefix}⏭️ ${actor} пропускает ход${reason}`;
    }
    case 'evolution':
      return `${roundPrefix}✨ Эволюция — условия выполнены!`;
    case 'miss': {
      const actor = getCombatantLabel(entry.source, panels);
      return `${roundPrefix}💨 ${actor} промахивается`;
    }
    case 'summon_expired': {
      const expired = Array.isArray(entry.data) ? entry.data : [];
      if (!expired.length) {
        return `${roundPrefix}👻 Призванные юниты исчезают`;
      }
      const names = expired.map(item => getCombatantLabel(item.id, panels));
      return `${roundPrefix}👻 ${names.join(', ')} исчезает`;
    }
    default:
      return `${roundPrefix}${entry.event || 'Событие'}`;
  }
}

export function buildBattleTimeline(battle, panels) {
  const hpState = panels.map(panel => ({
    id: panel.id,
    team: panel.team,
    hp: panel.maxHp,
    maxHp: panel.maxHp,
    energy: 100, // Начальная энергия
    maxEnergy: 100
  }));
  const hpMap = new Map(hpState.map(item => [item.id, item]));
  const timeline = [];

  timeline.push({
    description: '⚡ Бой начинается!',
    entry: { event: 'start', round: 0 },
    snapshot: cloneHpSnapshot(hpState)
  });

  (battle.log || []).forEach(entry => {
    // Обновляем HP при атаке
    if (entry.event === 'attack' && hpMap.has(entry.target)) {
      const targetState = hpMap.get(entry.target);
      const damage = Math.max(0, Math.round(entry.value ?? 0));
      targetState.hp = Math.max(0, targetState.hp - damage);
    }
    
    // Обновляем энергию при использовании способности
    if (entry.energy && hpMap.has(entry.source)) {
      const sourceState = hpMap.get(entry.source);
      sourceState.energy = entry.energy.after ?? sourceState.energy;
    }
    
    // Восстановление энергии в начале хода (симулируем)
    // Это будет обрабатываться в snapshot при рендеринге
    
    const description = formatBattlePlaybackEntry(entry, panels);
    timeline.push({
      description,
      entry,
      snapshot: cloneHpSnapshot(hpState)
    });
  });

  timeline.push({
    description: '🏁 Бой завершён.',
    entry: { event: 'end', round: battle.round ?? null },
    snapshot: cloneHpSnapshot(hpState)
  });

  return timeline;
}

/**
 * Строит таймлайны статусов для каждого шага боя
 */
export function buildStatusTimeline(battle, timeline) {
  if (!battle || !timeline) return {};
  
  const statusTimeline = {};
  const statusMap = new Map(); // combatantId -> Map<statusType, statusData>
  
  timeline.forEach((step, stepIndex) => {
    const entry = step.entry;
    if (!entry) return;
    
    // Обрабатываем события применения статусов
    if (entry.statusEffects) {
      entry.statusEffects.forEach(statusEffect => {
        const combatantId = String(statusEffect.target || statusEffect.combatantId || '');
        if (!combatantId) return;
        
        if (!statusMap.has(combatantId)) {
          statusMap.set(combatantId, new Map());
        }
        const combatantStatuses = statusMap.get(combatantId);
        
        // Добавляем или обновляем статус
        combatantStatuses.set(statusEffect.type, {
          type: statusEffect.type,
          expiresAt: statusEffect.expiresAtRound,
          data: statusEffect.data || {}
        });
      });
    }
    
    // Обрабатываем события удаления статусов
    if (entry.removedStatusEffects) {
      entry.removedStatusEffects.forEach(statusEffect => {
        const combatantId = String(statusEffect.target || statusEffect.combatantId || '');
        if (!combatantId) return;
        
        const combatantStatuses = statusMap.get(combatantId);
        if (combatantStatuses) {
          combatantStatuses.delete(statusEffect.type);
        }
      });
    }
    
    // Сохраняем снимок статусов для этого шага
    statusMap.forEach((statuses, combatantId) => {
      if (!statusTimeline[stepIndex]) {
        statusTimeline[stepIndex] = { statuses: {} };
      }
      if (!statusTimeline[stepIndex].statuses[combatantId]) {
        statusTimeline[stepIndex].statuses[combatantId] = [];
      }
      
      // Конвертируем Map в массив
      const statusArray = Array.from(statuses.values());
      statusTimeline[stepIndex].statuses[combatantId] = statusArray;
    });
  });
  
  return statusTimeline;
}

/**
 * Определяет текущий ход и действие из timeline
 * Этап 3: Динамическое отображение карт
 */
export function getCurrentTurnAndAction(timeline, currentStep, panels) {
  if (!timeline || !Array.isArray(timeline) || timeline.length === 0) {
    return { turn: 'A', action: null };
  }
  
  const step = Math.min(Math.max(currentStep, 0), timeline.length - 1);
  const current = timeline[step];
  
  if (!current || !current.entry) {
    return { turn: 'A', action: null };
  }
  
  const entry = current.entry;
  const source = entry?.source;
  
  // Определяем команду по источнику события
  let turn = 'A';
  if (source != null && panels && Array.isArray(panels)) {
    const sourcePanel = panels.find(p => String(p.id) === String(source));
    if (sourcePanel) {
      turn = sourcePanel.team || 'A';
    }
  }
  
  // Определяем действие (атака, способность)
  let action = null;
  if (entry.event === 'attack' || entry.event === 'ability_use' || entry.event === 'attack_blocked') {
    const attacker = source;
    const defender = entry.target || null;
    
    if (attacker != null && defender != null) {
      action = {
        attacker: String(attacker),
        defender: String(defender)
      };
    }
  }
  
  return { turn, action };
}

/**
 * Строит телеметрию по раундам для боя
 */
export function buildTelemetryByRound(battle, panels) {
  if (!battle || !panels) return {};
  
  const telemetryByRound = {};
  
  // Обрабатываем события из battle.log
  (battle.log || []).forEach(entry => {
    const round = entry.round ?? 0;
    if (!telemetryByRound[round]) {
      telemetryByRound[round] = [];
    }
    
    // Форматируем сообщение телеметрии
    let message = '';
    const roundPrefix = round > 0 ? `[Раунд ${round}] ` : '';
    
    switch (entry.event) {
      case 'attack':
        message = `${roundPrefix}Атака: ${entry.source} → ${entry.target}`;
        if (entry.value) {
          message += ` (урон: ${Math.round(entry.value)})`;
        }
        break;
      case 'ability_used':
        message = `${roundPrefix}Способность использована: ${entry.abilityId || 'unknown'}`;
        break;
      case 'status_applied':
        message = `${roundPrefix}Статус применён: ${entry.statusType || 'unknown'}`;
        break;
      case 'shield_activated':
        message = `${roundPrefix}Щит активирован`;
        break;
      default:
        message = `${roundPrefix}${entry.event || 'Событие'}`;
    }
    
    if (message) {
      telemetryByRound[round].push(message);
    }
  });
  
  return telemetryByRound;
}
